import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { frase } from '../../../../frase';
import { ascoltaSkin } from '../../../../skin';
import { clientIdTelegram, mantieProntoTelegram, confermaConTelegram, preparaTelegram, ritornoAutomaticoTelegram, avviaRitornoTelegram } from '../../../../services/telegramAccesso';
import useTrascinaGiu from '../../../../utils/useTrascinaGiu';
import './FinestraAccesso.css';
import '../AvvisoSessione.css';

/**
 * ACCEDI: una finestra sopra la pagina, non una pagina nuova.
 *
 * Chi clicca Accedi quasi sempre stava guardando una quota o aveva gia' il
 * coupon pieno. La pagina /login se lo porta via e poi deve riportarlo
 * indietro, e il coupon nel frattempo o lo si tiene o lo si perde. La
 * finestra lascia tutto dov'e': si chiude e si e' ancora li'.
 *
 * La pagina /login resta dov'e' e funziona: serve ancora quando si arriva da
 * un link diretto o da una sessione scaduta su un indirizzo protetto.
 *
 * Una sola finestra per tutto il sito. Dalla barra, dal coupon, da una
 * sessione scaduta: cambia la riga sotto il titolo, non la finestra.
 *
 * ACCEDI CON TELEGRAM sta qui, sotto «Entra», solo se la skin lo ha acceso
 * (Client ID). Il gettone che esce e' quello della piattaforma, lo stesso
 * della password: vedi UserService.doLoginTelegram.
 *
 * DOPO L'ACCESSO: se si e' arrivati da un link diretto o da una pagina
 * riservata (route /login → AccessoDaLink), si torna dove si voleva andare
 * (location.state.dopoAccesso).
 *
 * SUL TELEFONO sale dal basso, e si chiude anche trascinandola giu' dalla
 * maniglia o dal titolo. `onRegistrati`, quando c'e', prende il posto della
 * pagina /register per «Registrati» (la skin Nova apre il suo pannello).
 */

const MOTIVI = {
  barra: frase('Il conto, le giocate e i movimenti.'),
  coupon: frase('Entra per confermare la giocata: il coupon resta com’è.'),
  scaduta: frase('La sessione è scaduta. Rientra e torni dov’eri.'),
};

/**
 * `dettaglio` e' il PERCHE' la sessione e' finita, come lo scrive il custode
 * (services/sessione.js): { titolo, testo }. Quando c'e', il titolo della
 * finestra e' quello — «Sessione scaduta per inattività», «Durata massima
 * raggiunta», «Sessione chiusa da un nuovo accesso», «Sessione chiusa da un
 * operatore» — mai solo «Accedi». Il codice arriva dal gateway, il testo dal
 * catalogo in sessione.js.
 */
function FinestraAccesso({ aperta, motivo = 'barra', dettaglio = null, userService, onChiudi, onRegistrati }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const campoUtente = useRef(null);
  // l'ultima posizione, per l'effetto che sta in ascolto dell'accesso senza ricrearlo
  const posizione = useRef(location);
  posizione.current = location;
  const andataDopo = useRef(false);

  // La skin puo' arrivare dopo la pagina: quando arriva si ridisegna, e il
  // pulsante di Telegram compare senza ricaricare.
  const [, rileggiSkin] = useState(0);
  useEffect(() => ascoltaSkin(() => rileggiSkin((n) => n + 1)), []);
  const telegramClientId = clientIdTelegram();

  const [utente, setUtente] = useState('');
  const [password, setPassword] = useState('');
  const [mostraPassword, setMostraPassword] = useState(false);
  const [guaio, setGuaio] = useState('');
  const [inCorso, setInCorso] = useState(false);

  const chiudi = useCallback(() => {
    setGuaio('');
    setPassword('');
    setInCorso(false);
    // Chiusa senza entrare: la richiesta «dopo l'accesso» non vale piu', e non
    // deve portare altrove chi accedera' piu' tardi dalla barra.
    const st = posizione.current.state;
    if (!andataDopo.current && st && st.dopoAccesso) {
      navigate(`${posizione.current.pathname}${posizione.current.search || ''}`, { replace: true, state: null });
    }
    andataDopo.current = false;
    if (typeof onChiudi === 'function') onChiudi();
  }, [onChiudi, navigate]);
  const [finestra, presa] = useTrascinaGiu(chiudi);

  // L'errore vero arriva dal servizio, non lo scrive la finestra. Quando
  // l'accesso riesce la finestra si chiude e la pagina resta quella: e' il
  // motivo per cui e' una finestra e non una pagina.
  useEffect(() => {
    if (!aperta || !userService) return undefined;

    const stop = new Subject();

    userService.isLoggedIn().pipe(takeUntil(stop)).subscribe((dentro) => {
      if (!dentro) return;
      // arrivati da un link diretto o da una pagina riservata: si va dove si voleva
      const dopo = posizione.current.state && posizione.current.state.dopoAccesso;
      if (dopo && dopo.pathname) {
        andataDopo.current = true;
        navigate(`${dopo.pathname}${dopo.search || ''}${dopo.hash || ''}`, { replace: true });
      }
      chiudi();
    });

    if (typeof userService.handleErrorMessage === 'function') {
      userService.handleErrorMessage().pipe(takeUntil(stop)).subscribe((messaggio) => {
        if (messaggio) {
          setGuaio(messaggio);
          setInCorso(false);
        }
      });
    }

    return () => {
      stop.next();
      stop.complete();
    };
  }, [aperta, userService, chiudi, navigate]);

  // Il nonce e la libreria di Telegram si tengono pronti mentre la finestra e'
  // aperta: il clic deve aprire la finestra di Telegram subito, o il browser la blocca.
  useEffect(() => {
    if (!aperta || !telegramClientId) return undefined;
    return mantieProntoTelegram();
  }, [aperta, telegramClientId]);

  // Il fuoco nel campo solo da scrivania: su un telefono farebbe salire la
  // tastiera prima ancora che si veda cosa si e' aperto.
  useEffect(() => {
    if (!aperta) return undefined;
    setGuaio('');
    if (typeof userService?.clearErrorMessage === 'function') userService.clearErrorMessage();

    const daScrivania = window.matchMedia('(hover: hover)').matches;
    const t = daScrivania
        ? setTimeout(() => campoUtente.current && campoUtente.current.focus(), 60)
        : null;

    const allaTastiera = (e) => { if (e.key === 'Escape') chiudi(); };
    document.addEventListener('keydown', allaTastiera);

    // La pagina dietro non deve scorrere mentre la finestra e' aperta.
    const eraCosi = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      if (t) clearTimeout(t);
      document.removeEventListener('keydown', allaTastiera);
      document.body.style.overflow = eraCosi;
    };
  }, [aperta, userService, chiudi]);

  if (!aperta) return null;

  const manda = (e) => {
    e.preventDefault();
    if (!utente.trim() || !password) {
      setGuaio(t('Compila utente e password.'));
      return;
    }
    setGuaio('');
    setInCorso(true);
    userService.doLogin(utente.trim(), password);
  };

  const vaiA = (dove) => { chiudi(); navigate(dove); };

  const entraConTelegram = async () => {
    if (inCorso) return;
    setGuaio('');
    setInCorso(true);
    if (ritornoAutomaticoTelegram()) {
      // IL RITORNO AUTOMATICO: si va su /login (registrato in BotFather) e da li' da
      // Telegram; Telegram riporta su /login col codice, e AccessoDaLink entra e porta
      // dove si voleva andare (dopoAccesso) o alla home.
      const dopo = posizione.current.state && posizione.current.state.dopoAccesso;
      try {
        const dove = dopo && dopo.pathname ? `${dopo.pathname}${dopo.search || ''}${dopo.hash || ''}` : '/';
        window.history.replaceState(window.history.state, '', '/login');
        await avviaRitornoTelegram('accesso', dove);
      } catch (err) {
        window.history.replaceState(window.history.state, '', posizione.current.pathname);
        setInCorso(false);
        setGuaio((err && err.message) || t('Telegram non ha risposto'));
      }
      return;
    }
    try {
      const { idToken, nonce, user } = await confermaConTelegram();
      // l'esito arriva da userService: isLoggedIn (la finestra si chiude) o il
      // messaggio d'errore (si legge sopra i campi)
      const esito = await userService.doLoginTelegram(idToken, nonce);
      if (esito && esito.esito === 'senzaConto') {
        // NESSUN CONTO con questo Telegram: si passa alla registrazione, gia'
        // compilata con i suoi dati, e l'avviso dice di proseguire da li'.
        setInCorso(false);
        chiudi();
        if (typeof userService.richiediRegistrazione === 'function') userService.richiediRegistrazione('', { idToken, nonce, user }, true);
        return;
      }
    } catch (err) {
      setInCorso(false);
      if (!(err && err.annullato)) setGuaio((err && err.message) || t('Telegram non ha risposto'));
    } finally {
      // il nonce e' usa e getta: se ne prepara un altro per il prossimo clic
      preparaTelegram().catch(() => { /* si riprova al clic */ });
    }
  };

  return (
      <div
          className="velo-accesso aperto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="titolo-accesso"
          // Cliccare fuori chiude; cliccare dentro no: il velo prende il
          // click solo se e' arrivato al velo stesso.
          onMouseDown={(e) => { if (e.target === e.currentTarget) chiudi(); }}
      >
        <form ref={finestra} className="finestra-accesso" onSubmit={manda} autoComplete="on">
          <span className="maniglia-accesso" aria-hidden="true" {...presa} />

          <div className="testa-accesso" {...presa}>
            <div>
              {dettaglio ? (
                  <>
                    <h3 id="titolo-accesso" className="titolo-scaduta">
                      <span className="punto-scaduta" aria-hidden="true" />
                      {t(dettaglio.titolo)}
                    </h3>
                    <p className="testo-scaduta">{t(dettaglio.testo, dettaglio.valori || {})}</p>
                  </>
              ) : (
                  <>
                    <h3 id="titolo-accesso">{t('Accedi')}</h3>
                    <p>{t(MOTIVI[motivo] || MOTIVI.barra)}</p>
                  </>
              )}
            </div>
            <button type="button" className="chiudi-accesso" onClick={chiudi} aria-label={t('Chiudi')}>
              &#10005;
            </button>
          </div>

          {/* L'errore sta SOPRA i campi, dove l'occhio torna dopo aver
              premuto, e dice una cosa sola. */}
          {guaio ? (
              <div className="guaio-accesso" role="alert">
                <span aria-hidden="true">&#9888;</span>
                <span>{guaio}</span>
              </div>
          ) : null}

          <div className="campo-accesso">
            <label htmlFor="accesso-utente">{t('Utente')}</label>
            <div className="guaina-accesso">
              <input
                  id="accesso-utente"
                  ref={campoUtente}
                  type="text"
                  name="username"
                  value={utente}
                  onChange={(e) => setUtente(e.target.value)}
                  placeholder={t('Il tuo nome utente')}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck="false"
              />
            </div>
          </div>

          <div className="campo-accesso segreto">
            <label htmlFor="accesso-password">{t('Password')}</label>
            <div className="guaina-accesso">
              <input
                  id="accesso-password"
                  type={mostraPassword ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('La tua password')}
                  autoComplete="current-password"
              />
              <button
                  type="button"
                  className="sbircia-accesso"
                  onClick={() => setMostraPassword((m) => !m)}
              >
                {mostraPassword ? t('Nascondi') : t('Mostra')}
              </button>
            </div>
          </div>

          <button type="button" className="scordata-accesso" onClick={() => vaiA('/forgot-password')}>
            {t('Password dimenticata?')}
          </button>

          <button type="submit" className="entra-accesso" disabled={inCorso}>
            {inCorso ? t('Un attimo…') : t('Entra')}
          </button>

          {telegramClientId ? (
              <>
                <div className="oppure-accesso" aria-hidden="true">{t('oppure')}</div>
                <button type="button" className="telegram-accesso" onClick={entraConTelegram} disabled={inCorso}
                        data-testid="accedi-con-telegram">
                  <svg className="icona-telegram-accesso" width="20" height="20" viewBox="0 0 22 22" aria-hidden="true" focusable="false">
                    <path fill="currentColor" d="M2.81376 10.5943C7.36914 8.61002 10.406 7.30175 11.9245 6.66971C16.265 4.86489 17.1658 4.55143 17.7542 4.54094C17.8836 4.53874 18.1716 4.57072 18.3596 4.72281C18.5158 4.85097 18.5598 5.0243 18.5818 5.14588C18.6013 5.26745 18.6282 5.54454 18.6062 5.76083C18.3719 8.23138 17.3539 14.2266 16.8363 16.9938C16.619 18.1647 16.1869 18.5572 15.7695 18.5955C14.8613 18.679 14.1728 17.996 13.294 17.42C11.9196 16.5185 11.1433 15.9575 9.80793 15.0779C8.26505 14.0613 9.26594 13.5025 10.1448 12.5895C10.3743 12.3505 14.373 8.71426 14.4487 8.38445C14.4585 8.34319 14.4683 8.18939 14.3755 8.10834C14.2852 8.02705 14.1508 8.05488 14.0532 8.07685C13.914 8.1081 11.7193 9.56015 7.4618 12.4328C6.83928 12.861 6.27536 13.0697 5.76758 13.0587C5.21097 13.0468 4.13689 12.7433 3.3386 12.484C2.3621 12.166 1.5833 11.9977 1.65166 11.4575C1.68583 11.1763 2.07406 10.8884 2.81376 10.5943Z"/>
                  </svg>
                  {t('Accedi con Telegram')}
                </button>
              </>
          ) : null}

          <div className="pie-accesso">
            {t('Non hai un conto?')}{' '}
            <button type="button" onClick={() => {
              if (typeof onRegistrati !== 'function') { vaiA('/register'); return; }
              chiudi();
              onRegistrati();
            }}>{t('Registrati')}</button>
          </div>
        </form>
      </div>
  );
}

export default FinestraAccesso;
